import * as React from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Wrapper from "../../../components/Wrapper";
import { useNavigate } from "react-router-dom";
import "./LandlordFunctionality.css";
import {
  Box,
  Breadcrumbs,
  Grid,
  Paper,
  Stack,
  Container,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from "@mui/material";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import HomeIcon          from "@mui/icons-material/Home";
import DescriptionIcon   from "@mui/icons-material/Description";
import UploadFileIcon    from "@mui/icons-material/UploadFile";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import HistoryIcon       from "@mui/icons-material/History";
import PersonRemoveIcon  from "@mui/icons-material/PersonRemove";
import ArrowForwardIcon  from "@mui/icons-material/ArrowForward";
import NavigateNextIcon  from "@mui/icons-material/NavigateNext";
import { BASE_URL }      from "../../../config/config";
import axios             from "axios";

import TenantInfoBanner  from "../../../components/TenantInfoBannerOwner";



export default function LandlordFunctionality() {
  const open     = useSelector((state) => state.sidebar.open);
  const navigate = useNavigate();
  const [openUnregisterModal, setOpenUnregisterModal] = React.useState(false);
  const [dueDate, setDueDate] = React.useState("");
  const [reason,  setReason]  = React.useState("");

  // ✅ IDs from URL
  const getId = window.location.pathname.replace("/landlord-functionality/", "");
  const [tenantId, propertyId] = getId.split("/");

  // ✅ Redux data
  const flats = useSelector(
    (state) => state?.validateUser?.UserValidate?.data?.user?.flats
  );
  const validateUSER = useSelector(
    (state) => state?.validateUser?.UserValidate?.data
  );

  // ✅ Find matched property
  const matchedProperty = flats
    ?.flatMap((item) =>
      (item.propertyDetail || []).map((property) => ({
        ...property,
        bill_uploaded_by: property.bill_uploaded_by,
      }))
    )
    ?.find((property) => String(property._id) === String(propertyId));

  const isBillUploadedByLandlord = matchedProperty?.bill_uploaded_by === "landlord";

  // ✅ Find tenant detail
  const user     = validateUSER?.user;
  const requests = user?.requests || [];

  const tenantDetail = requests.find(
    (req) =>
      String(req.tenantId)  === String(tenantId) &&
      String(req.flatId)    === String(propertyId)
  );

  // 🔁 Fallback tenant detail
  const fallbackTenantDetail = flats
    ?.flatMap((item) => item.propertyDetail || [])
    ?.find(
      (property) =>
        String(property._id)      === String(propertyId) &&
        String(property.tenantId) === String(tenantId)
    );

  const displayTenant = tenantDetail || fallbackTenantDetail;

  // Function cards
  const functionCards = [
    {
      title: "Agreement",
      description: "View and manage rental agreements with your tenant",
      path: `/agreement/${getId}`,
      icon: DescriptionIcon,
      gradient: "linear-gradient(135deg, var(--owner-primary) 0%, var(--owner-nav) 100%)",
      iconBg: "color-mix(in srgb, var(--owner-primary) 15%, transparent)",
      show: true,
    },
    {
      title: "Upload Bill",
      description: "Upload monthly utility bills for your tenant",
      path: `/upload-Bill/${getId}`,
      icon: UploadFileIcon,
      gradient: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
      iconBg: "rgba(240, 147, 251, 0.15)",
      show: isBillUploadedByLandlord === true,
    },
    {
      title: "Pending Bill",
      description: "Track and manage pending bill payments",
      path: `/pending-bill/${getId}`,
      icon: PendingActionsIcon,
      gradient: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
      iconBg: "rgba(250, 112, 154, 0.15)",
      show: true,
    },
    {
      title: "Previous Bill",
      description: "View billing history and past payments",
      path: `/previous-bill/${getId}`,
      icon: HistoryIcon,
      gradient: "linear-gradient(135deg, #30cfd0 0%, #330867 100%)",
      iconBg: "rgba(48, 207, 208, 0.15)",
      show: true,
    },
    {
      title: "Unregister",
      description: "Remove tenant registration from property",
      path: `/unregister-tenant/${getId}`,
      icon: PersonRemoveIcon,
      gradient: "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)",
      iconBg: "rgba(168, 237, 234, 0.15)",
      show: true,
    },
  ];

  const handleUnregister = async (action = "CREATE") => {
    try {
      const token =
        localStorage.getItem("token") || sessionStorage.getItem("ots_token");

      if (action === "CREATE") {
        if (!dueDate || !reason) {
          alert("Please select due date and enter reason");
          return;
        }
      }

      const payload = { action };
      if (action === "CREATE") {
        payload.dueDate = dueDate;
        payload.reason  = reason;
      }

      const { data } = await axios.post(
        `${BASE_URL}/unregister-tenant/${tenantId}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (action === "CREATE") {
        setOpenUnregisterModal(false);
        setDueDate("");
        setReason("");
      }
      console.log(data.message);
    } catch (error) {
      console.error(error.response?.data || error.message);
    }
  };

  return (
    <>
      <Wrapper open={open} />
      <Box className={`main-container ${open ? "sidebar-open" : "sidebar-closed"}`}>
        <Container maxWidth="xl" sx={{ py: 3 }}>

          {/* Breadcrumbs */}
          <Paper elevation={0} className="breadcrumb-wrapper">
            <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 0 }}>
              <Link className="breadcrumb-link" to="/landlord-dashboard">
                <HomeIcon sx={{ mr: 0.5, fontSize: 20 }} />
                <span>Home</span>
              </Link>
              <Link className="breadcrumb-link" to="/my-tenants">
                <span>My Tenants</span>
              </Link>
              <Typography className="breadcrumb-current">Manage Services</Typography>
            </Breadcrumbs>
          </Paper>

          {/* ✅ Tenant Info — reusable banner */}
          <TenantInfoBanner tenant={displayTenant} />

          {/* Section Title */}
          <Box className="section-header">
            <Typography variant="h5" className="section-title">
              Manage Tenant Services
            </Typography>
            <Typography variant="body2" className="section-subtitle">
              Access all tenant management tools and services
            </Typography>
          </Box>

          {/* Function Cards */}
          <Grid container spacing={3} className="cards-container">
            {functionCards
              .filter((card) => card.show)
              .map((card, index) => {
                const IconComponent = card.icon;
                return (
                  <Grid item xs={12} sm={6} lg={4} key={index}>
                    <Card
                      className="function-card"
                      onClick={() => {
                        if (card.onClick) card.onClick();
                        else navigate(card.path);
                      }}
                    >
                      <Box
                        className="card-gradient-header"
                        sx={{ background: card.gradient }}
                      >
                        <Box className="card-icon-large">
                          <IconComponent sx={{ fontSize: 56 }} />
                        </Box>
                        <Box className="gradient-overlay" />
                      </Box>

                      <CardContent className="card-content">
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          mb={1.5}
                        >
                          <Box className="card-icon-small" sx={{ background: card.iconBg }}>
                            <IconComponent sx={{ fontSize: 24 }} />
                          </Box>
                          <IconButton size="small" className="card-arrow">
                            <ArrowForwardIcon fontSize="small" />
                          </IconButton>
                        </Stack>

                        <Typography variant="h6" className="card-title">
                          {card.title}
                        </Typography>
                        <Typography variant="body2" className="card-description">
                          {card.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
          </Grid>
        </Container>
      </Box>

      {/* Unregister Modal */}
      <Dialog
        open={openUnregisterModal}
        onClose={() => setOpenUnregisterModal(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Request Tenant Unregister</DialogTitle>
        <DialogContent>
          <Typography mb={2}>
            Please provide the effective date and reason for unregistering this tenant.
          </Typography>
          <TextField
            label="Effective Date"
            type="date"
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            sx={{ mb: 3 }}
          />
          <TextField
            label="Reason"
            multiline
            rows={4}
            fullWidth
            required
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Enter reason for unregister request..."
          />
          <Box mt={3}>
            <Typography color="error">
              This will send a request to the tenant. The tenant must accept before removal is completed.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenUnregisterModal(false)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={!dueDate || !reason}
            onClick={() => handleUnregister("CREATE")}
          >
            Send Request
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}